import { Tag, Close } from '@icon-park/react'
import { SearchResponse } from '@shared/models'
import { Tokens } from '@/assets/tokens'
import { Alert, Api } from '@/modules'
import { Button, Flex, Selector } from '@/components'
import { Str } from '@/utils/strings'
import { useEffect, useMemo, useState } from 'react'
import { AddThemeContainer, AddThemeOverlay } from './EditModal.styles'

interface AddThemeModalProps {
  companyID: string
  brandID: string
  currentMentionThemes: string[]
  alreadyAddedThemes: string[]
  onCancel: () => void
  onAdd: (themeInternalID: string, themeName: string, themeGroupInternalID: string, themeGroupName: string) => void
}

export function AddThemeModal({
  companyID,
  brandID,
  currentMentionThemes,
  alreadyAddedThemes,
  onCancel,
  onAdd,
}: AddThemeModalProps): JSX.Element {
  const { sendAlert } = Alert.Hook()

  const [allThemeGroups, setAllThemeGroups] = useState<SearchResponse[]>([])
  const [themes, setThemes] = useState<SearchResponse[]>([])
  const [loadingThemes, setLoadingThemes] = useState(false)

  const { binder: bindThemeGroup, selectedOption: selectedThemeGroup } = Selector.Binder()
  const { binder: bindTheme, selectedOption: selectedTheme, reset: resetTheme } = Selector.Binder()

  const [searchThemeGroupsAPI, isLoadingThemeGroups] = Api.PromiseQuery<SearchResponse[]>(`theme-groups/search`)
  const [searchThemesAPI] = Api.PromiseQuery<SearchResponse[]>(`themes/search`)

  const availableThemes = useMemo(() => {
    if (!themes || !selectedThemeGroup) return []

    const allUsedThemes = [...currentMentionThemes, ...alreadyAddedThemes]
    return themes.filter((theme) => !allUsedThemes.includes(String(theme.value)))
  }, [themes, currentMentionThemes, alreadyAddedThemes, selectedThemeGroup])

  useEffect(() => {
    loadThemeGroups()
  }, [])

  const loadThemeGroups = async () => {
    try {
      const [data] = await searchThemeGroupsAPI({
        company: companyID,
        size: 10000,
        orderBy: 'name:ASC',
        brandIDs: [brandID],
      })
      if (data) {
        setAllThemeGroups(data)
      }
    } catch (error) {
      sendAlert({
        type: 'error',
        title: 'Erro ao carregar grupos temáticos',
        message: 'Não foi possível carregar os grupos temáticos.',
      })
    }
  }

  useEffect(() => {
    if (selectedThemeGroup) {
      loadThemesForGroup(String(selectedThemeGroup.value))
      resetTheme()
    } else {
      setThemes([])
      resetTheme()
    }
  }, [selectedThemeGroup])

  const loadThemesForGroup = async (themeGroupId: string) => {
    setLoadingThemes(true)
    try {
      const [data] = await searchThemesAPI({
        company: companyID,
        marca: brandID,
        grupoTematico: themeGroupId.toString(),
        size: 10000,
        fathersOnly: true,
        orderBy: 'value:ASC',
      })
      if (data) {
        setThemes(data)
      }
    } catch (error) {
      sendAlert({
        type: 'error',
        title: 'Erro ao carregar temas',
        message: `Não foi possível carregar os temas para o grupo selecionado.`,
      })
    } finally {
      setLoadingThemes(false)
    }
  }

  const handleAdd = () => {
    if (!selectedThemeGroup || !selectedTheme) {
      sendAlert({
        type: 'error',
        title: 'Seleção incompleta',
        message: 'Por favor, selecione um grupo temático e um tema.',
      })
      return
    }

    const selectedGroupName =
      allThemeGroups.find((group) => group.value === selectedThemeGroup.value)?.text || selectedThemeGroup.text
    const selectedThemeName = availableThemes.find((t) => t.value === selectedTheme.value)?.text || selectedTheme.text

    onAdd(String(selectedTheme.value), selectedThemeName, String(selectedThemeGroup.value), selectedGroupName)
  }

  return (
    <AddThemeOverlay>
      <AddThemeContainer>
        <Flex $type="row" $justify="center" $align="center" $fit style={{ position: 'relative' }}>
          <h3 style={{ margin: 0, textAlign: 'center', flex: 1 }}>Adicionar Novo Tema</h3>
          <Button.Root $variant="link" onClick={onCancel} style={{ position: 'absolute', right: 0 }}>
            <Button.Icon value={Close} color={Tokens.ColorTextPrimary} />
          </Button.Root>
        </Flex>

        <Flex $type="column" $justify="center" $align="center" $fit $gap={16}>
          <Selector.Default
            {...bindThemeGroup}
            placeholder="Selecionar Grupo Temático"
            options={allThemeGroups.map((group) => ({
              text: Str.from(group.text).initCap,
              value: group.value,
            }))}
            loading={isLoadingThemeGroups}>
            <Selector.Icon value={Tag} color={Tokens.ColorUIPrimary} />
          </Selector.Default>

          <Selector.Default
            {...bindTheme}
            placeholder={
              !selectedThemeGroup ? 'Selecione um grupo temático primeiro'
              : availableThemes.length === 0 && !loadingThemes ?
                'Todos os temas deste grupo já estão associados'
              : 'Selecionar Tema'
            }
            options={availableThemes.map((theme) => ({
              text: Str.from(theme.text).initCap,
              value: theme.value,
            }))}
            loading={loadingThemes}>
            <Selector.Icon value={Tag} color={Tokens.ColorUIPrimary} />
          </Selector.Default>

          {selectedThemeGroup && availableThemes.length === 0 && !loadingThemes && (
            <p style={{ color: Tokens.ColorTextTertiary, fontSize: '14px', textAlign: 'center', margin: 0 }}>
              Todos os temas deste grupo já estão associados a esta menção.
            </p>
          )}
        </Flex>

        <Flex $type="row" $justify="center" $align="center" $fit $gap={16}>
          <Button.Root $variant="secondary" onClick={onCancel} style={{ width: '7rem' }}>
            <Button.Text value="Cancelar" />
          </Button.Root>
          <Button.Root
            $variant="primary"
            onClick={handleAdd}
            style={{ width: '7rem' }}
            disabled={!selectedThemeGroup || !selectedTheme || availableThemes.length === 0}>
            <Button.Text value="Adicionar" />
          </Button.Root>
        </Flex>
      </AddThemeContainer>
    </AddThemeOverlay>
  )
}
